/*
 * SCALPIUMM landing section — behaviour.
 * Rewritten from the standalone index.html for Shopify/Horizon: the simulated cart, language and currency
 * switchers are replaced by the real Ajax Cart API, Shopify Markets and Translate & Adapt.
 * Config comes from the <script data-scalpiumm-config> JSON rendered by sections/scalpiumm-landing.liquid.
 * Loaded as a module so it can use the theme's @shopify/events contract for cart updates.
 */
import { CartLinesUpdateEvent } from '@shopify/events';
import { lockScroll, unlockScroll } from '@theme/utilities';

{
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(root) {
    if (!root || root.dataset.scalpiummReady) return;
    const configNode = root.querySelector('[data-scalpiumm-config]');
    if (!configNode) return;
    root.dataset.scalpiummReady = 'true';

    // Global listeners and long-lived timers are released together when the section unloads.
    const controller = new AbortController();
    const { signal } = controller;

    // Horizon scrolls .page-wrapper instead of the window on wide screens (base.css, min-width: 990px),
    // so scroll-driven effects read whichever element is actually scrolling.
    const pageWrapper = document.querySelector('.page-wrapper');
    const scroller = () => (pageWrapper && pageWrapper.scrollHeight > pageWrapper.clientHeight + 1
      && getComputedStyle(pageWrapper).overflowY !== 'visible' ? pageWrapper : null);
    const scrollTop = () => scroller()?.scrollTop ?? window.scrollY;
    const scrollMax = () => {
      const el = scroller();
      return el ? el.scrollHeight - el.clientHeight : document.documentElement.scrollHeight - window.innerHeight;
    };
    const onScroll = (fn) => {
      window.addEventListener('scroll', fn, { passive: true, signal });
      pageWrapper?.addEventListener('scroll', fn, { passive: true, signal });
    };
    const timers = new Set();
    root.scalpiummCleanup = () => {
      controller.abort();
      timers.forEach((id) => { clearTimeout(id); clearInterval(id); });
      timers.clear();
      const openPopupNode = root.querySelector('[data-popup]');
      if (openPopupNode) unlockScroll(openPopupNode);
    };

    const cfg = JSON.parse(configNode.textContent);
    const designMode = Boolean(window.Shopify && window.Shopify.designMode);
    const gsap = root.hasAttribute('data-animations') && !reduceMotion ? window.gsap : null;
    const ScrollTrigger = gsap ? window.ScrollTrigger : null;
    const $ = (selector) => root.querySelector(selector);
    const $$ = (selector) => [...root.querySelectorAll(selector)];

    // Prices rendered by Liquid are already in the buyer's currency; settings (threshold, shipping) are in shop currency.
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    const currency = window.Shopify?.currency?.active || cfg.currency || 'PLN';
    let formatter;
    try {
      formatter = new Intl.NumberFormat(cfg.locale || 'pl', { style: 'currency', currency });
    } catch (_) {
      formatter = new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' });
    }
    const money = (cents) => formatter.format((Number(cents) || 0) / 100);
    const toLocal = (shopCents) => Math.round((Number(shopCents) || 0) * rate);
    const fill = (template, values) =>
      String(template || '').replace(/\{(\w+)\}/g, (match, key) => (key in values ? values[key] : match));
    const threshold = toLocal(cfg.threshold);

    let selected = Number(cfg.selected) === 1 ? 1 : 2;

    /* ---------- Variant / set selection ---------- */
    const form = $('.scalpiumm-form');
    const variantInput = $('[data-variant-input]');
    const quantityInput = $('[data-quantity-input]');
    const addButton = $('[data-add]');
    const addError = $('[data-add-error]');
    const photo = $('[data-hero-photo]');
    const position = $('[data-gallery-position]');
    const stickyThumb = $('[data-sticky-thumb]');
    const micro = $('[data-micro]');

    function updateMicro() {
      if (!micro) return;
      const price = cfg.options[selected].price;
      const free = price >= threshold;
      micro.textContent = free
        ? fill(cfg.text.microFree, { threshold: money(threshold) })
        : fill(cfg.text.microPaid, { shipping: money(toLocal(cfg.shippingCost)), gap: money(threshold - price) });
      micro.classList.toggle('is-free', free);
    }

    let swapId = 0;
    let photoBusy = false;
    let lastGalleryAction = performance.now();
    const fullClip = 'polygon(0 0,100% 0,100% 100%,0 100%)';
    const waveClip = 'polygon(0 0,100% 0,100% 18%,94% 27%,100% 36%,95% 47%,100% 58%,92% 69%,100% 79%,100% 100%,0 100%)';

    function swapPhoto(src, alt) {
      if (!photo || !src || photo.getAttribute('src') === src) return;
      lastGalleryAction = performance.now();
      const id = ++swapId;
      if (!gsap) {
        photo.src = src;
        photo.alt = alt || '';
        return;
      }
      photoBusy = true;
      gsap.killTweensOf(photo);
      gsap.to(photo, {
        opacity: 0, filter: 'blur(10px)', scale: 0.97, rotationY: -10, clipPath: waveClip,
        duration: 0.34, ease: 'power2.in', overwrite: true,
        onComplete: () => {
          if (id !== swapId) return;
          photo.src = src;
          photo.alt = alt || '';
          const reveal = () => {
            if (id !== swapId) return;
            gsap.fromTo(photo,
              { opacity: 0, filter: 'blur(10px)', scale: 1.08, rotationY: 10, clipPath: waveClip },
              {
                opacity: 1, filter: 'blur(0px)', scale: 1, rotationY: 0, clipPath: fullClip,
                duration: 0.62, ease: 'power3.out', overwrite: true,
                onComplete: () => { photoBusy = false; lastGalleryAction = performance.now(); },
              });
          };
          if (photo.complete && photo.naturalWidth) reveal();
          else {
            photo.addEventListener('load', reveal, { once: true });
            photo.addEventListener('error', reveal, { once: true });
          }
        },
      });
    }

    function select(next) {
      const n = Number(next) === 1 ? 1 : 2;
      const option = cfg.options[n];
      if (!option || !option.variantId) return;
      selected = n;
      $$('.bundle-option').forEach((button) => {
        const active = Number(button.dataset.option) === n;
        button.classList.toggle('is-selected', active);
        button.setAttribute('aria-checked', String(active));
      });
      $$('[data-sticky-bar] [data-option]').forEach((button) =>
        button.classList.toggle('is-selected', Number(button.dataset.option) === n));
      $$('[data-price]').forEach((el) => { el.textContent = option.priceFormatted; });
      if (variantInput) variantInput.value = option.variantId;
      if (quantityInput) quantityInput.value = option.quantity;
      if (addButton) {
        addButton.disabled = !option.available;
        addButton.textContent = option.available ? option.cta : cfg.text.soldOut;
      }
      if (position) position.textContent = `${n} / 2`;
      if (stickyThumb) stickyThumb.src = option.image;
      updateMicro();
      swapPhoto(option.image, option.alt);
    }

    $$('.bundle-option').forEach((button) => button.addEventListener('click', () => select(button.dataset.option)));
    $$('[data-sticky-bar] [data-option]').forEach((button) =>
      button.addEventListener('click', () => select(button.dataset.option)));

    // Gallery arrows and swipe toggle between the two sets, exactly like the original page.
    const heroArt = $('.hero-art');
    $$('[data-gallery-step]').forEach((button) => {
      button.addEventListener('click', () => {
        lastGalleryAction = performance.now();
        button.classList.remove('is-rippling');
        void button.offsetWidth;
        button.classList.add('is-rippling');
        setTimeout(() => button.classList.remove('is-rippling'), 720);
        if (gsap) {
          gsap.killTweensOf(button);
          gsap.fromTo(button, { scale: 0.85 }, { scale: 1, duration: 0.78, ease: 'elastic.out(1,.42)', overwrite: true });
        }
        select(selected === 1 ? 2 : 1);
      });
      if (gsap) {
        button.addEventListener('pointermove', (event) => {
          if (event.pointerType === 'touch') return;
          const r = button.getBoundingClientRect();
          gsap.to(button, {
            x: (event.clientX - (r.left + r.width / 2)) * 0.16,
            y: (event.clientY - (r.top + r.height / 2)) * 0.16,
            duration: 0.32, ease: 'power2.out', overwrite: 'auto',
          });
        });
        button.addEventListener('pointerleave', () =>
          gsap.to(button, { x: 0, y: 0, duration: 0.65, ease: 'elastic.out(1,.35)', overwrite: 'auto' }));
      }
    });

    if (heroArt) {
      let touchX = null;
      heroArt.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch' && !event.target.closest('.gallery-arrow')) touchX = event.clientX;
      }, { passive: true, signal });
      heroArt.addEventListener('pointerup', (event) => {
        if (touchX === null) return;
        const delta = event.clientX - touchX;
        touchX = null;
        if (Math.abs(delta) < 48) return;
        select(selected === 1 ? 2 : 1);
      }, { passive: true, signal });
      heroArt.addEventListener('pointercancel', () => { touchX = null; }, { passive: true, signal });

      if (gsap && photo) {
        heroArt.addEventListener('pointermove', (event) => {
          if (photoBusy || performance.now() - lastGalleryAction < 4000 || event.pointerType === 'touch') return;
          const r = heroArt.getBoundingClientRect();
          const nx = (event.clientX - r.left) / r.width - 0.5;
          const ny = (event.clientY - r.top) / r.height - 0.5;
          gsap.to(photo, {
            rotationY: nx * 3.2, rotationX: -ny * 2.2, scale: 1.014, transformPerspective: 1100,
            duration: 0.42, ease: 'power2.out', overwrite: 'auto',
          });
        });
        heroArt.addEventListener('pointerleave', () => {
          if (!photoBusy) {
            gsap.to(photo, {
              rotationX: 0, rotationY: 0, scale: 1, transformPerspective: 1100,
              duration: 0.58, ease: 'power2.out', overwrite: 'auto',
            });
          }
        });
      }
    }

    // Amounts derived from the free-shipping threshold must follow the buyer's currency.
    if (rate !== 1) {
      updateMicro();
      $$('[data-fill-threshold]').forEach((el) => {
        el.textContent = fill(el.dataset.fillThreshold, { threshold: money(threshold) });
      });
    }

    /* ---------- Add to cart (real Shopify cart) ---------- */
    const fetchJSON = async (url, options = {}) => {
      const headers = { Accept: 'application/json' };
      if (options.body) headers['Content-Type'] = 'application/json';
      const response = await fetch(url, { ...options, headers });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.description || data.message || cfg.text.addError);
      return data;
    };
    const getCart = () => fetchJSON(cfg.routes.cartJs);

    /**
     * Runs a cart mutation and announces it with the theme's CartLinesUpdateEvent, so Horizon's cart icon,
     * cart drawer and cart page re-render. The event is dispatched before the request and settles with the
     * resulting cart, matching product-form.js. Action 'update' (not 'add') keeps Horizon's "auto-open cart
     * drawer" setting from opening the drawer on top of this section's own "added to cart" popup.
     * @param {Array<{merchandiseId: string, quantity: number}>} lines
     * @param {() => Promise<any>} work - resolves with the Ajax cart after the mutation
     */
    async function withCartEvent(lines, work) {
      const deferred = CartLinesUpdateEvent.createPromise();
      root.dispatchEvent(new CartLinesUpdateEvent({
        action: 'update',
        context: 'product',
        lines,
        promise: deferred.promise,
      }));
      try {
        const cart = await work();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'scalpiumm',
            sourceId: root.dataset.sectionId,
            itemCount: cart.item_count,
            didError: false,
          },
        });
        return cart;
      } catch (error) {
        deferred.reject(error);
        throw error;
      }
    }

    const popup = $('[data-popup]');
    const popupState = { option: selected, packs: 1, busy: false };

    async function addToCart() {
      const option = cfg.options[selected];
      if (!option || !option.available || !addButton) return;
      const label = addButton.textContent;
      addButton.disabled = true;
      addButton.textContent = cfg.text.adding;
      if (addError) addError.textContent = '';
      try {
        const cart = await withCartEvent(
          [{ merchandiseId: String(option.variantId), quantity: option.quantity }],
          async () => {
            await fetchJSON(cfg.routes.cartAdd, {
              method: 'POST',
              body: JSON.stringify({ items: [{ id: option.variantId, quantity: option.quantity }] }),
            });
            return getCart();
          },
        );
        if (cfg.afterAdd === 'checkout') { window.location.assign(cfg.routes.checkout); return; }
        if (cfg.afterAdd === 'cart' || !popup) { window.location.assign(cfg.routes.cart); return; }
        popupState.option = selected;
        popupState.packs = 1;
        openPopup(cart);
      } catch (error) {
        if (addError) addError.textContent = error.message || cfg.text.addError;
      } finally {
        addButton.disabled = !option.available;
        addButton.textContent = label;
      }
    }

    if (form) {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        addToCart();
      });
    }

    /* ---------- "Added to cart" popup ---------- */
    let popupReturnFocus = null;

    function renderPopup(cart) {
      if (!popup) return;
      const option = cfg.options[popupState.option];
      const packs = popupState.packs;
      const set = (selector, text) => { const el = popup.querySelector(selector); if (el) el.textContent = text; };

      set('[data-popup-variant]', option.label);
      set('[data-popup-qty]', String(packs));
      set('[data-popup-line-price]', money(option.price * packs));
      const old = popup.querySelector('[data-popup-old-price]');
      if (old) {
        old.hidden = !(option.compareAt > option.price);
        old.textContent = option.compareAt > option.price ? money(option.compareAt * packs) : '';
      }
      const saved = option.compareAt > option.price ? (option.compareAt - option.price) * packs : 0;
      set('[data-popup-saving]', saved > 0 ? fill(cfg.text.saving, { saving: money(saved) }) : '');
      const photoNode = popup.querySelector('[data-popup-photo]');
      if (photoNode) { photoNode.src = option.image; photoNode.alt = option.label; }

      if (cart) {
        set('[data-popup-total]', money(cart.total_price));
        const shipping = popup.querySelector('[data-popup-shipping]');
        if (shipping && threshold > 0) {
          const free = cart.total_price >= threshold;
          shipping.textContent = free
            ? cfg.text.shipFree
            : fill(cfg.text.shipGap, { gap: money(threshold - cart.total_price), threshold: money(threshold) });
          const percent = Math.min(100, Math.round((cart.total_price / threshold) * 100));
          const fillBar = popup.querySelector('[data-popup-progress-fill]');
          if (fillBar) fillBar.style.width = `${percent}%`;
          popup.querySelector('[data-popup-progress]')?.setAttribute('aria-valuenow', String(percent));
        }
      }

      const one = cfg.options[1];
      const two = cfg.options[2];
      const upsell = popup.querySelector('[data-popup-upsell]');
      const canUpsell = popupState.option === 1 && two.available && two.variantId && two.price < one.price * 2;
      if (upsell) {
        upsell.hidden = !canUpsell;
        if (canUpsell) {
          set('[data-popup-upsell-text]', fill(cfg.text.upsell, {
            extra: money(Math.max(0, two.price - one.price)),
            two: money(two.price),
            saving: money(Math.max(0, one.price * 2 - two.price)),
          }));
        }
      }
      const trial = popup.querySelector('[data-popup-trial]');
      if (trial) trial.hidden = !(popupState.option === 2 && one.available && one.variantId);
    }

    function openPopup(cart) {
      if (!popup) return;
      popupReturnFocus = document.activeElement;
      renderPopup(cart);
      popup.hidden = false;
      lockScroll(popup);
      requestAnimationFrame(() => popup.querySelector('.popup-close')?.focus());
    }

    function closePopup() {
      if (!popup || popup.hidden) return;
      popup.hidden = true;
      unlockScroll(popup);
      popupReturnFocus?.focus?.();
    }

    // Changes only the units this popup added: (packs × units per set) of the chosen variant.
    async function changeLine(variantId, deltaUnits, cart) {
      if (!deltaUnits) return cart;
      if (deltaUnits > 0) {
        await fetchJSON(cfg.routes.cartAdd, {
          method: 'POST',
          body: JSON.stringify({ items: [{ id: variantId, quantity: deltaUnits }] }),
        });
        return getCart();
      }
      const line = (cart || (await getCart())).items.find((item) => item.variant_id === Number(variantId));
      if (!line) return getCart();
      return fetchJSON(cfg.routes.cartChange, {
        method: 'POST',
        body: JSON.stringify({ id: line.key, quantity: Math.max(0, line.quantity + deltaUnits) }),
      });
    }

    async function updatePopup(nextOption, nextPacks) {
      if (popupState.busy) return;
      popupState.busy = true;
      popup?.classList.add('is-updating');
      try {
        const current = cfg.options[popupState.option];
        const next = cfg.options[nextOption];
        const cart = await withCartEvent(
          [{ merchandiseId: String(next.variantId), quantity: nextPacks * next.quantity }],
          async () => {
            let result = await getCart();
            if (current.variantId === next.variantId) {
              result = await changeLine(next.variantId, nextPacks * next.quantity - popupState.packs * current.quantity, result);
            } else {
              result = await changeLine(current.variantId, -popupState.packs * current.quantity, result);
              result = await changeLine(next.variantId, nextPacks * next.quantity, result);
            }
            return result && result.items ? result : getCart();
          },
        );
        popupState.option = nextOption;
        popupState.packs = nextPacks;
        if (nextOption !== selected) select(nextOption);
        renderPopup(cart);
      } catch (error) {
        const shipping = popup?.querySelector('[data-popup-shipping]');
        if (shipping) shipping.textContent = error.message || cfg.text.addError;
      } finally {
        popupState.busy = false;
        popup?.classList.remove('is-updating');
      }
    }

    if (popup) {
      popup.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', closePopup));
      popup.querySelector('[data-popup-plus]')?.addEventListener('click', () =>
        updatePopup(popupState.option, Math.min(20, popupState.packs + 1)));
      popup.querySelector('[data-popup-minus]')?.addEventListener('click', () => {
        if (popupState.packs > 1) updatePopup(popupState.option, popupState.packs - 1);
      });
      popup.querySelector('[data-popup-upsell-choose]')?.addEventListener('click', () => updatePopup(2, 1));
      popup.querySelector('[data-popup-trial]')?.addEventListener('click', () => updatePopup(1, popupState.packs));
      document.addEventListener('keydown', (event) => {
        if (popup.hidden) return;
        if (event.key === 'Escape') { closePopup(); return; }
        if (event.key !== 'Tab') return;
        const focusable = [...popup.querySelectorAll('button:not([disabled]),a[href]')].filter((el) => el.offsetParent !== null);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }, { signal });
    }

    /* ---------- Sticky buy bar ---------- */
    const stickyBar = $('[data-sticky-bar]');
    const hero = $('.hero');
    const purchaseCard = $('#zestawy');
    if (stickyBar && hero) {
      const footer = document.querySelector('.shopify-section-group-footer-group, footer');
      const check = () => {
        const past = hero.getBoundingClientRect().bottom < 0;
        const atFooter = footer && footer.getBoundingClientRect().top < window.innerHeight;
        stickyBar.classList.toggle('is-visible', past && !atFooter);
      };
      onScroll(check);
      window.addEventListener('resize', check, { passive: true, signal });
      check();
      $('[data-sticky-cta]')?.addEventListener('click', (event) => {
        event.preventDefault();
        purchaseCard?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      });
    }

    const scrollToOffer = () => {
      select(2);
      purchaseCard?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    };

    /* ---------- Flip cards (steps) + gold spark burst ---------- */
    let burst = null;
    if (!reduceMotion) {
      const canvas = document.createElement('canvas');
      canvas.className = 'step-spark-canvas';
      canvas.setAttribute('aria-hidden', 'true');
      root.append(canvas);
      const ctx = canvas.getContext('2d');
      let dpr = 1;
      let w = 0;
      let h = 0;
      let particles = [];
      let raf = 0;
      const resize = () => {
        dpr = Math.min(window.devicePixelRatio || 1, 2);
        w = window.innerWidth;
        h = window.innerHeight;
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      };
      resize();
      window.addEventListener('resize', resize, { passive: true, signal });
      const draw = () => {
        ctx.clearRect(0, 0, w, h);
        particles = particles.filter((p) => p.life > 0.03);
        for (const p of particles) {
          p.x += p.vx; p.y += p.vy; p.vy += 0.055; p.vx *= 0.986; p.life -= 0.024;
          ctx.save();
          ctx.globalAlpha = p.life;
          ctx.translate(p.x, p.y);
          ctx.rotate((p.spin += 0.08));
          ctx.fillStyle = Math.random() > 0.22 ? '#d4af63' : '#fff0bd';
          ctx.shadowColor = '#d8ad52';
          ctx.shadowBlur = 8;
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 1.8);
          ctx.restore();
        }
        if (particles.length) raf = requestAnimationFrame(draw);
        else { raf = 0; ctx.clearRect(0, 0, w, h); }
      };
      burst = (card) => {
        const r = card.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        for (let i = 0; i < 34; i++) {
          const a = Math.random() * Math.PI * 2;
          const s = 1.4 + Math.random() * 4.1;
          particles.push({
            x: cx + Math.cos(a) * r.width * 0.34,
            y: cy + Math.sin(a) * Math.min(r.height * 0.3, 95),
            vx: Math.cos(a) * s, vy: Math.sin(a) * s - 1.1,
            life: 1, size: 1.2 + Math.random() * 2.4, spin: Math.random() * 6.2,
          });
        }
        if (navigator.vibrate) navigator.vibrate(50);
        if (!raf) raf = requestAnimationFrame(draw);
      };
    }

    $$('.steps .flip-card').forEach((card) => {
      const n = card.dataset.step;
      const label = () =>
        `Krok ${Number(n)}: ${card.classList.contains('is-flipped') ? cfg.text.flipClose : cfg.text.flipOpen}`;
      const flip = () => {
        const on = card.classList.toggle('is-flipped');
        card.setAttribute('aria-pressed', String(on));
        card.querySelector('.step-back')?.setAttribute('aria-hidden', String(on));
        card.querySelector('.step-front')?.setAttribute('aria-hidden', String(!on));
        card.setAttribute('aria-label', label());
        if (on && burst) burst(card);
      };
      card.addEventListener('click', flip);
      card.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); flip(); }
      });
    });

    /* ---------- FAQ: native <details> with animated answer ---------- */
    $$('.faq details').forEach((item) => {
      const sync = () => item.classList.toggle('is-active', item.open);
      item.addEventListener('toggle', sync);
      sync();
    });

    /* ---------- Reviews marquee (duplicate set for a seamless loop) ---------- */
    const track = $('[data-review-track]');
    if (track) {
      const cards = [...track.children].filter((card) => card.classList.contains('review'));
      if (cards.length) {
        const original = document.createElement('div');
        original.className = 'marquee-set';
        cards.forEach((card) => original.append(card));
        const duplicate = original.cloneNode(true);
        duplicate.setAttribute('aria-hidden', 'true');
        duplicate.querySelectorAll('[data-shopify-editor-block]').forEach((el) => el.removeAttribute('data-shopify-editor-block'));
        track.replaceChildren(original, duplicate);
        track.classList.add('marquee-ready');
      }
    }

    /* ---------- Scroll progress, reveal on scroll, hero parallax ---------- */
    const progress = $('[data-scroll-progress]');
    let scrollPending = false;
    onScroll(() => {
      if (scrollPending) return;
      scrollPending = true;
      requestAnimationFrame(() => {
        const y = scrollTop();
        const max = scrollMax();
        if (progress) progress.style.width = `${max > 0 ? (y / max) * 100 : 0}%`;
        if (heroArt && y < 700) heroArt.style.setProperty('--hero-shift', `${Math.min(18, y * 0.025)}px`);
        scrollPending = false;
      });
    });

    if ('IntersectionObserver' in window && !reduceMotion) {
      root.classList.add('motion');
      const inView = new IntersectionObserver((entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('in-view'); inView.unobserve(entry.target); }
      }), { threshold: 0.12 });
      $$('.content,.closing,.trustbar').forEach((el) => inView.observe(el));

      const targets = $$('.content,.closing,.trustbar,.hero-copy');
      targets.forEach((el) => el.classList.add('scroll-reveal'));
      const reveal = new IntersectionObserver((entries) => entries.forEach((entry) => {
        entry.target.classList.toggle('is-visible', entry.isIntersecting);
      }), { threshold: 0.12, rootMargin: '0px 0px -5% 0px' });
      targets.forEach((el) => reveal.observe(el));
    }

    /* ---------- Benefit cards: 3D tilt ---------- */
    $$('#benefits .benefit').forEach((card) => {
      if (reduceMotion) return;
      const state = { rx: 0, ry: 0, lift: 0, scale: 1 };
      if (gsap) card.classList.add('benefit-motion-ready');
      let frame = 0;
      const apply = () => {
        card.style.setProperty('--benefit-rx', `${state.rx.toFixed(2)}deg`);
        card.style.setProperty('--benefit-ry', `${state.ry.toFixed(2)}deg`);
        card.style.setProperty('--benefit-lift', `${(-state.lift).toFixed(2)}px`);
        card.style.setProperty('--benefit-scale', state.scale.toFixed(3));
      };
      const animate = (values) => {
        if (gsap) gsap.to(state, { ...values, duration: 0.42, ease: 'power3.out', overwrite: true, onUpdate: apply });
        else { Object.assign(state, values); apply(); }
      };
      card.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'touch') animate({ lift: 10, scale: 1.03 });
      });
      card.addEventListener('pointermove', (event) => {
        if (event.pointerType === 'touch') return;
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const r = card.getBoundingClientRect();
          const dx = (event.clientX - r.left) / r.width - 0.5;
          const dy = (event.clientY - r.top) / r.height - 0.5;
          animate({ rx: -dy * 9, ry: dx * 9, lift: 10, scale: 1.03 });
        });
      });
      card.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        cancelAnimationFrame(frame);
        animate({ rx: 0, ry: 0, lift: 0, scale: 1 });
      });
      card.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'touch') return;
        card.classList.add('is-touch-active');
        setTimeout(() => card.classList.remove('is-touch-active'), 850);
      });
    });

    /* ---------- GSAP layer (hero, reveals, magnetic buttons) ---------- */
    if (gsap) {
      if (ScrollTrigger) gsap.registerPlugin(ScrollTrigger);
      root.classList.add('motion-enabled');

      const heroBg = $('.hero-bg');
      if (heroBg) {
        gsap.fromTo(heroBg, { scale: 1 },
          { scale: 1.08, duration: 20, repeat: -1, yoyo: true, ease: 'sine.inOut', transformOrigin: 'center center' });
      }

      const lines = $$('.hero-title .hero-title-line');
      lines.forEach((line) => {
        const words = line.textContent.trim().split(/\s+/);
        line.replaceChildren();
        words.forEach((word, index) => {
          const clip = document.createElement('span');
          const inner = document.createElement('span');
          clip.className = 'hero-word-clip';
          inner.className = 'hero-word';
          inner.textContent = word;
          clip.append(inner);
          line.append(clip);
          if (index < words.length - 1) line.append(document.createTextNode(' '));
        });
      });
      if (lines.length) {
        gsap.fromTo($$('.hero-title .hero-word'), { y: 100, opacity: 0 },
          { y: 0, opacity: 1, stagger: 0.08, duration: 1, ease: 'power4.out', delay: 0.2, overwrite: true });
      }

      if (ScrollTrigger) {
        $$('.benefit,.compare .card,.marquee-set:not([aria-hidden="true"]) .review').forEach((el, i) =>
          gsap.from(el, {
            y: 26, opacity: 0, duration: 0.75, delay: (i % 4) * 0.06, ease: 'power3.out',
            scrollTrigger: { trigger: el, start: 'top 88%', once: true },
          }));
        window.addEventListener('load', () => ScrollTrigger.refresh(), { signal });
      }

      $$('.btn,.bundle-option').forEach((button) => {
        button.classList.add('btn-magnetic');
        button.addEventListener('mousemove', (event) => {
          const { left, top, width, height } = button.getBoundingClientRect();
          gsap.to(button, {
            x: (event.clientX - (left + width / 2)) * 0.3,
            y: (event.clientY - (top + height / 2)) * 0.3,
            duration: 0.4, ease: 'power2.out', overwrite: true,
          });
        });
        button.addEventListener('mouseleave', () =>
          gsap.to(button, { x: 0, y: 0, duration: 0.8, ease: 'elastic.out(1,0.3)', overwrite: true }));
      });
    }

    /* ---------- Offer reminders (disabled inside the theme editor) ---------- */
    if (designMode) return;

    const offerToast = cfg.text.offerToast;
    if (offerToast && offerToast.enabled) {
      const toast = document.createElement('aside');
      toast.className = 'offer-toast';
      toast.hidden = true;
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.innerHTML =
        '<span class="offer-toast-mark" aria-hidden="true">✦</span>' +
        '<div class="offer-toast-copy"><span class="offer-toast-tag"></span><strong class="offer-toast-title"></strong>' +
        '<span class="offer-toast-body"></span><a class="offer-toast-cta" href="#zestawy"></a></div>' +
        '<button type="button" class="offer-toast-close" aria-label="Zamknij powiadomienie">×</button>';
      toast.querySelector('.offer-toast-tag').textContent = offerToast.tag;
      toast.querySelector('.offer-toast-title').textContent = offerToast.title;
      toast.querySelector('.offer-toast-body').textContent = offerToast.body;
      toast.querySelector('.offer-toast-cta').textContent = offerToast.cta;
      root.append(toast);
      let hideTimer;
      let nextTimer;
      let shown = 0;
      const scheduleNext = () => {
        clearTimeout(nextTimer);
        if (shown >= 3) return;
        nextTimer = setTimeout(show, 45000 + Math.random() * 20000);
        timers.add(nextTimer);
      };
      const hide = () => {
        toast.classList.remove('is-visible');
        hideTimer = setTimeout(() => { toast.hidden = true; scheduleNext(); }, 320);
      };
      function show() {
        if (document.hidden || (popup && !popup.hidden)) { scheduleNext(); return; }
        shown++;
        toast.hidden = false;
        requestAnimationFrame(() => toast.classList.add('is-visible'));
        hideTimer = setTimeout(hide, 5000);
      }
      toast.querySelector('.offer-toast-close').addEventListener('click', () => { clearTimeout(hideTimer); hide(); });
      toast.querySelector('.offer-toast-cta').addEventListener('click', (event) => {
        event.preventDefault();
        clearTimeout(hideTimer);
        hide();
        scrollToOffer();
      });
      timers.add(setTimeout(show, 10000));
    }

    const salesToast = cfg.text.salesToast;
    if (salesToast && salesToast.enabled) {
      const toast = document.createElement('aside');
      toast.className = 'sales-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.innerHTML =
        '<span class="sales-toast-icon" aria-hidden="true">✦</span>' +
        '<div class="sales-toast-copy"><strong></strong><span class="sales-toast-message"></span>' +
        '<a href="#zestawy" class="sales-toast-link"></a></div>' +
        '<button class="sales-toast-close" type="button" aria-label="Zamknij powiadomienie">×</button>';
      toast.querySelector('strong').textContent = salesToast.brand;
      toast.querySelector('.sales-toast-message').textContent = salesToast.message;
      toast.querySelector('.sales-toast-link').textContent = salesToast.cta;
      root.append(toast);
      let dismissed = false;
      let hideTimer;
      const showToast = () => {
        if (dismissed || document.hidden || (popup && !popup.hidden)) return;
        toast.classList.add('is-visible');
        clearTimeout(hideTimer);
        hideTimer = setTimeout(() => toast.classList.remove('is-visible'), 4000);
      };
      toast.querySelector('.sales-toast-close').addEventListener('click', () => {
        dismissed = true;
        toast.classList.remove('is-visible');
      });
      toast.querySelector('.sales-toast-link').addEventListener('click', (event) => {
        event.preventDefault();
        scrollToOffer();
      });
      timers.add(setTimeout(() => { showToast(); timers.add(setInterval(showToast, 10000)); }, 10000));
    }
  }

  const boot = () => document.querySelectorAll('[data-scalpiumm]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', (event) => init(event.target.querySelector('[data-scalpiumm]')));
  // Theme editor: drop window/document listeners and timers of the instance being replaced.
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelector('[data-scalpiumm]')?.scalpiummCleanup?.();
  });
}
